import { useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { useAtom } from 'jotai'
import { Pencil } from 'lucide-react'
import { Button, Interactive, Markdown, Popover, type MarkdownFeatures } from '@highlight/ui'
import TextArea from '@renderer/components/TextArea'
import { FloatingSaveControls } from '@renderer/components/floating-save-controls'
import { summaryDraftAtom, summaryEditStatusAtom, useSummarySave } from '@renderer/hooks/audio/summary-draft'
import { useCmdEnterSave } from '@renderer/hooks/use-cmd-enter-save'
import { reviseAudioNoteSummary } from '@renderer/utils/audio-notes/revise-summary'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'

const log = GlobalLogger.bind('MeetingSummaryEditor')

/** A summary has no images to paste, so the editor takes the rest of the markdown set. */
const SUMMARY_EDITOR_FEATURES: MarkdownFeatures = { image: false }

interface SummaryEditorProps {
  audioNoteId: string
  /** The summary as saved on the note, which is what the public link shows when there is one. */
  savedText: string
}

/**
 * A finalized summary, editable in place. Edits land in the draft, and only the save controls write
 * them to the note, so the saved summary and any public link stay put until then. A revision from
 * the Edit with prompt popover replaces the draft's text.
 */
export function SummaryEditor({ audioNoteId, savedText }: SummaryEditorProps) {
  const [draft, setDraft] = useAtom(summaryDraftAtom(audioNoteId))
  const [status, setStatus] = useAtom(summaryEditStatusAtom(audioNoteId))
  const [isPromptOpen, setIsPromptOpen] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [revisionError, setRevisionError] = useState<string | null>(null)

  const revise = async () => {
    const instruction = prompt.trim()
    if (!instruction || status.isBusy) {
      return
    }
    setStatus((current) => ({ ...current, isBusy: true, saveError: null }))
    setRevisionError(null)
    try {
      const text = await reviseAudioNoteSummary(audioNoteId, instruction, draft?.text ?? savedText)
      setDraft({ text, instruction })
      setPrompt('')
      setIsPromptOpen(false)
      AmplitudeRenderer.trackEvent('Audio Note Action', { action: 'revise summary', trigger: 'sidepanel meeting view' })
    } catch (error) {
      log.error(`Failed to revise summary for audio note ${audioNoteId}: ${String(error)}`)
      setRevisionError(error instanceof Error ? error.message : 'Could not revise the summary.')
    } finally {
      setStatus((current) => ({ ...current, isBusy: false }))
    }
  }

  const onPromptKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Stop propagation so a revision never reaches a staged action's (cmd/ctrl+)enter invoke handler (HIG-5238).
      event.preventDefault()
      event.stopPropagation()
      void revise()
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <Markdown
        editable
        value={draft?.text ?? savedText}
        onChange={(markdown, editor) => {
          // Programmatic changes also reach here (a revision landing, the editor normalising its markdown),
          // so only the user's own typing counts as a draft edit.
          if (editor.isFocused) {
            setDraft({ text: markdown, instruction: draft?.instruction ?? null })
          }
        }}
        features={SUMMARY_EDITOR_FEATURES}
        readOnly={status.isBusy}
      />

      <div className="flex justify-start">
        {/* Stays open during a revision so the spinner and any error stay visible. */}
        <Popover open={isPromptOpen} onOpenChange={(open) => !status.isBusy && setIsPromptOpen(open)}>
          <Popover.Trigger
            render={
              <button
                type="button"
                className="type-prose-sm text-foreground-secondary hover:text-foreground inline-flex items-center gap-1.5 bg-transparent p-0"
              >
                <Pencil aria-hidden="true" className="size-3.5" />
                Edit with prompt
              </button>
            }
          />
          <Popover.Content showArrow={false} side="top" align="start" sideOffset={8} className="w-80 p-3">
            <Interactive>
              <div className="flex flex-col gap-2">
                <p className="text-foreground-placeholder text-xs">What should change?</p>
                <TextArea
                  value={prompt}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setPrompt(event.target.value)}
                  onKeyDown={onPromptKeyDown}
                  placeholder="Describe a change, e.g. shorter, or only action items"
                  readOnly={status.isBusy}
                  autoFocus
                  autoResize
                  size="medium"
                  className="max-h-[120px] resize-none"
                  aria-label="Describe a change to the summary"
                />
                {revisionError && <p className="text-danger-subtle type-prose-sm">{revisionError}</p>}
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void revise()}
                    loading={status.isBusy}
                    disabled={!prompt.trim() || status.isBusy}
                    className="ml-auto"
                  >
                    Apply Edits
                  </Button>
                </div>
              </div>
            </Interactive>
          </Popover.Content>
        </Popover>
      </div>
    </div>
  )
}

/**
 * The save controls for an unsaved summary. The meeting view's floating bar mounts them in place of
 * its usual row.
 */
export function SummarySaveControls({
  audioNoteId,
  savedText,
  isPublished,
}: {
  audioNoteId: string
  savedText: string
  isPublished: boolean
}) {
  const { isBusy, saveError, save, discard } = useSummarySave(audioNoteId, savedText, isPublished)
  useCmdEnterSave(!isBusy, save)

  return (
    <FloatingSaveControls
      onSave={() => void save()}
      onDiscard={discard}
      isBusy={isBusy}
      className="flex-1"
      saveLabel={isPublished ? 'Save and publish summary' : 'Save summary'}
      saveTooltip={isPublished ? 'Anyone with the public link will see this change once it is published' : undefined}
    >
      {saveError && <p className="text-danger-subtle type-prose-sm">{saveError}</p>}
    </FloatingSaveControls>
  )
}
